<template>
  <div class="event-popup">
    <div class="event-popup__bg-wrapper">
      <div class="event-popup__bg"></div>
    </div>
    <div class="event-popup__scroll-container">
      <div class="event-popup__scroll-content">
        <EventPopupHeader :user-last-name="userLastName" :rest-count="restCount" />
        <Transition @after-leave="showNext">
          <EventPopupPanel v-if="showEvent" :item="currentItem" />
        </Transition>
      </div>
      <ClearSvg class="event-popup__close" @click="onClose" />
    </div>
    <EventPopupFooter @next="onNext" @click="onShowEntryModal" />
    <EventPopupEntryModal
      v-if="showEntryModal"
      :item="currentItem"
      :sending="sending"
      @close="onCloseEntryModal"
      @entry="onEntry"
    />
    <EventPopupEntryResultModal
      v-if="showResultModal && resultSchedule"
      :item="currentItem"
      :schedule="resultSchedule"
      @close="onCloseResultModal"
    />
    P<EventPopupFinishedModal v-if="showFinishedModal" @close="onCloseFinishedModal" />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from '@vue/composition-api';
import { EventPopupItem, EventSchedule } from 'oc2api/popup';
import EventPopupHeader from 'components/popup/EventPopup/EventPopupHeader.vue';
import EventPopupPanel from 'components/popup/EventPopup/EventPopupPanel.vue';
import EventPopupFooter from 'components/popup/EventPopup/EventPopupFooter.vue';
import EventPopupEntryModal, { EntryParams } from 'components/popup/EventPopup/EventPopupEntryModal.vue';
import EventPopupEntryResultModal from 'components/popup/EventPopup/EventPopupEntryResultModal.vue';
import EventPopupFinishedModal from 'components/popup/EventPopup/EventPopupFinishedModal.vue';
import ClearSvg from 'images/clear.svg';
import { sendEntryRequest } from 'oc2api/entries';

export default defineComponent({
  name: 'EventPopup',
  components: {
    EventPopupHeader,
    EventPopupPanel,
    EventPopupFooter,
    EventPopupEntryModal,
    EventPopupEntryResultModal,
    EventPopupFinishedModal,
    ClearSvg,
  },
  props: {
    userLastName: {
      type: String,
      required: true,
    },
    items: {
      type: Array as PropType<EventPopupItem[]>,
      required: true,
    },
  },
  emits: {
    close: () => true,
  },
  setup(props, { emit }) {
    const sending = ref(false);
    const showEvent = ref(true);
    const currentIndex = ref(0);
    const restCount = computed(() => props.items.length - currentIndex.value);
    const currentItem = computed(() => props.items[currentIndex.value]);
    const currentEvent = computed(() => currentItem.value.event);
    const onNext = () => {
      if (props.items.length > currentIndex.value + 1) {
        showEvent.value = false;
        document.getElementsByClassName('event-popup__scroll-container')[0].scrollTop = 0;
      } else {
        showFinishedModal.value = true;
      }
    };
    const showNext = () => {
      showEvent.value = true;
      currentIndex.value += 1;
    };
    const onClose = () => {
      emit('close');
    };

    const showEntryModal = ref(false);
    const onShowEntryModal = () => {
      showEntryModal.value = true;
    };
    const onCloseEntryModal = () => {
      showEntryModal.value = false;
    };

    const showResultModal = ref(false);
    const resultSchedule = ref<EventSchedule>();
    const onCloseResultModal = () => {
      showResultModal.value = false;
      onNext();
    };

    const onEntry = async (params: EntryParams) => {
      try {
        sending.value = true;

        await sendEntryRequest(params);
        const schedule = currentEvent.value.schedules.find((schedule) => schedule.id === params.scheduleId);
        resultSchedule.value = schedule;
        showEntryModal.value = false;
        showResultModal.value = true;

        sending.value = false;
      } catch (_error) {
        // TODO: Sentry に情報を送る
        alert('エラーが発生しました');
      } finally {
        sending.value = false;
      }
    };

    const showFinishedModal = ref(false);
    const onCloseFinishedModal = () => {
      showFinishedModal.value = false;
      onClose();
    };

    return {
      sending,
      currentItem,
      restCount,
      currentEvent,
      onNext,
      showNext,
      showEvent,
      onClose,
      showEntryModal,
      onShowEntryModal,
      onCloseEntryModal,
      onEntry,
      showResultModal,
      resultSchedule,
      onCloseResultModal,
      showFinishedModal,
      onCloseFinishedModal,
    };
  },
});
</script>

<style lang="scss" scoped>
@import 'stylesheets/mixins';
@import 'stylesheets/variables';

.event-popup {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: $z-index-popup;
  display: flex;
}

.event-popup__bg-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.event-popup__bg {
  margin: 0 auto;
  width: 640px;
  max-width: 100vw;
  min-height: 100vh;
  background-image: url('../images/bg_event_popup.jpg');
  background-repeat: repeat-y;

  @media screen and (max-width: 768px) {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    width: auto;
    max-width: none;
  }
}
.event-popup__scroll-container {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow-y: auto;
}

.event-popup__scroll-content {
  margin: 0 auto;
  width: 640px;
  max-width: 100vw;
  min-height: 100vh;
  padding: 12px;

  @media screen and (max-width: 768px) {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    width: auto;
    max-width: none;
  }
}

.event-popup__close {
  position: fixed;
  width: 24px;
  right: 27px;
  top: 27px;
  color: white;
  cursor: pointer;

  @media screen and (max-width: 768px) {
    position: absolute;
    right: 15px;
    top: 19px;
  }
}

.v-enter {
  opacity: 0;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.25s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
